import type { TranslationKey } from '@change-corgi/core/i18n';

import type { UploaderControls } from '#src/app/shared/hooks/uppyUploader';
import { getUploadErrorMessages, RestrictionUploadError } from '#src/app/shared/utils/uppyUploader';

import { MceImageError } from './mceImageError';

type BlobInfo = {
	id: () => string;
	name: () => string;
	filename: () => string;
	blob: () => Blob;
	base64: () => string;
	blobUri: () => string;
	uri: () => string | undefined;
};

export const acceptedRichMediaImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];

async function uploadAndGetPublicUrl(
	uploader: UploaderControls,
	setErrorKey: (errorKey: TranslationKey | null) => void,
): Promise<string> {
	try {
		const result = await uploader.upload();
		// `result` itself is genuinely `UploadResultType | undefined` (see uppyUploader/types.ts).
		// `.at(0)` (unlike the `[0]` index signature) is correctly typed `UppyFile | undefined`,
		// matching the real runtime possibility of an empty `successful` array.
		const filePublicUrl = result?.successful?.at(0)?.meta.filePublicUrl;
		if (typeof filePublicUrl !== 'string' || !filePublicUrl) {
			setErrorKey(getUploadErrorMessages().CATCH_ALL);
			throw new MceImageError({ remove: true });
		}
		return decodeURIComponent(filePublicUrl);
	} catch (error) {
		// A RestrictionUploadError (thrown by the post-compression size check) already had its
		// error key set via the centralized 'restriction-failed' listener (see useUploader.ts) by
		// the time Uppy's own upload() rejects with it — we just convert it to the removable MCE
		// error HugeRTE expects, without needing to know the specific reason here.
		if (error instanceof RestrictionUploadError) {
			throw new MceImageError({ remove: true });
		}
		throw error;
	}
}

export const imagesUploadHandler =
	({ uploader, setErrorKey }: { uploader: UploaderControls; setErrorKey: (errorKey: TranslationKey | null) => void }) =>
	async (blobInfo: BlobInfo): Promise<string> => {
		const blob = blobInfo.blob();
		if (!acceptedRichMediaImageTypes.includes(blob.type)) {
			setErrorKey(getUploadErrorMessages().UNSUPPORTED_FILE_TYPE);
			throw new MceImageError({ remove: true });
		}

		try {
			uploader.addFile(blob.type, blob);
		} catch {
			// uppy.addFile() throws synchronously on a restriction failure (e.g. the raw 20MB
			// ceiling); the 'restriction-failed' listener registered in useUploader already sets
			// the error key for it, so we just need to convert this into the removable MCE error
			// HugeRTE expects, rather than let the raw restriction error bubble out uncaught.
			throw new MceImageError({ remove: true });
		}
		return uploadAndGetPublicUrl(uploader, setErrorKey);
	};
